import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { IHotel } from 'app/typings';
import { ChannelManager } from 'app/typings/channelManagers';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import type { NewHotel } from 'app/typings/hotels/NewHotel/NewHotel';
import type { PackageCore } from 'app/typings/packages';
import type { PublishError } from 'app/typings/publishError';
import { isHttpApiError } from 'app/utils/error';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const duplicatePath = '/hotels/:hotelId/duplicate' as const;
const hotelIssuingPath = '/extranet/:hotelId/issuing/activate' as const;

export const hotelsPath = '/hotels' as const;

const hotelPackagesPath = '/hotels/:hotelId/packages' as const;
const hidePath = 'hotels/:hotelId/hide' as const;
const publishPath = 'hotels/:hotelId/publish' as const;
const unpublishPath = 'hotels/:hotelId/unpublish' as const;
const createPath = 'gateway/admin/hotels' as const;
const updatePath = 'gateway/admin/hotels/:hotelId' as const;

export const hotelPath = '/hotels/:hotelId' as const;

export function useGetHotel(hotelId?: number) {
  return useQuery<Hotel>({
    queryKey: [hotelPath, { hotelId }],
    queryFn: !!hotelId
      ? () =>
          apiRequest({
            path: hotelPath,
            params: { hotelId: hotelId?.toString() },
          })
            .catch((err) => message.error('Error fetching hotel', err))
            .then((hotel) => ({
              ...hotel,
              channelManager: hotel.channelManager || ChannelManager.NONE,
            })) as Promise<Hotel>
      : undefined,
    enabled: !!hotelId,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useSaveHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: NewHotel) =>
      apiMutation(
        'POST',
        {
          path: createPath,
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      message.success('Hotel successfully saved!');
      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: () => {
      message.error('Error saving Hotel');
    },
  });
}

export function useUpdateHotel(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<Hotel>) =>
      payload.id
        ? (apiMutation(
            'PATCH',
            {
              path: updatePath,
              params: { hotelId: hotelId.toString() },
            },
            payload
          ) as Promise<void>)
        : Promise.resolve(),
    onSuccess: () => {
      message.success('Hotel successfully updated!');
      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: (error: unknown) => {
      if (isHttpApiError(error)) {
        return;
      }

      message.error('Error updating hotel');
    },
  });
}

export function useActivateIssuingForHotel(hotelId: string) {
  return useMutation({
    mutationFn: async () =>
      apiMutation('POST', {
        path: hotelIssuingPath,
        params: { hotelId },
      }) as Promise<void>,
    onError: () => {
      message.error('Error activating issuing');
    },
  });
}

export type DuplicateHotelPayload = {
  name: string;
};

export function useDuplicateHotel(hotelId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DuplicateHotelPayload) =>
      apiMutation(
        'POST',
        {
          path: duplicatePath,
          params: { hotelId },
        },
        payload
      ) as Promise<{ hotelId: string }>,
    onSuccess: () => {
      message.success(`Hotel ${hotelId} duplicated, redirecting to copy`);
      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: () => {
      message.error('Failed to duplicate hotel');
    },
  });
}

export function useHotels() {
  return useQuery<IHotel[]>({
    queryKey: [hotelsPath],
    queryFn: () => apiRequest({ path: hotelsPath }),
    staleTime: Infinity,
  });
}

export function useGetPackageByHotelId(hotelId: number | null) {
  return useQuery({
    queryKey: [hotelPackagesPath, { hotelId }],
    queryFn: hotelId
      ? () =>
          apiRequest({
            path: hotelPackagesPath,
            params: { hotelId: hotelId.toString() },
          }) as Promise<{ packages: PackageCore[] }>
      : undefined,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
    onError: () => {
      message.error("Error fetching hotel's packages");
    },
  });
}

export function useHideHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { hotelId: number }) =>
      apiMutation('PATCH', {
        path: hidePath,
        params: {
          hotelId: payload.hotelId.toString(),
        },
      }) as Promise<{ hidden: boolean }>,
    onSuccess: (_, { hotelId }) => {
      message.success(`Hotel #${hotelId} hidden`);

      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: (_, { hotelId }) => {
      message.error(`Error hiding hotel ${hotelId}`);
    },
  });
}

export function useUnpublishHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { hotelId: number }) =>
      apiMutation('PATCH', {
        path: unpublishPath,
        params: {
          hotelId: payload.hotelId.toString(),
        },
      }) as Promise<{ hidden: boolean }>,
    onSuccess: (_, { hotelId }) => {
      message.success(`Hotel #${hotelId} unpublished`);

      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: (_, { hotelId }) => {
      message.error(`Error hiding hotel ${hotelId}`);
    },
  });
}

export function usePublishHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { hotelId: number }) =>
      apiMutation('PATCH', {
        path: publishPath,
        params: {
          hotelId: payload.hotelId.toString(),
        },
      }) as Promise<{ published: boolean }>,
    onSuccess: (_, payload) => {
      message.success(`Hotel #${payload.hotelId} published`);
      queryClient.invalidateQueries([hotelsPath]);
    },
    onError: (error: PublishError, payload) => {
      if (isHttpApiError(error)) {
        return;
      }

      if (error?.cause?.code === 400) {
        return;
      }

      message.error(`Error publishing hotel #${payload.hotelId}`);
    },
  });
}
